import { NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { UserTreeItemComponent } from 'src/app/components/users/user-tree-item/user-tree-item.component';
import { Context } from 'src/app/controls/advanced-gridview/models/context';
import { MenuITemAction } from 'src/app/controls/advanced-gridview/models/menu-item-action';
import { ConfirmDialogComponent } from 'src/app/controls/confirm-dialog/confirm-dialog.component';
import { ContextualMenuComponent } from 'src/app/controls/contextual-menu/contextual-menu.component';
import { ContextualMenuChange } from 'src/app/controls/contextual-menu/models/contextual-menu-change';
import { DefaultListComponent } from 'src/app/controls/list/default-list/default-list.component';
import { Culture } from 'src/app/models/culture';
import { Rights } from 'src/app/models/enums/rights';
import { Group } from 'src/app/models/group';
import { Role } from 'src/app/models/role';
import { User } from 'src/app/models/user';
import { GroupsService } from 'src/app/services/groups.service';
import { MemoryService } from 'src/app/services/memory.service';
import { UsersService } from 'src/app/services/users.service';
import { ContextualMenuComponent as ContextualMenuComponent_1 } from '../../../../controls/contextual-menu/contextual-menu.component';
import { UserTreeItemComponent as UserTreeItemComponent_1 } from '../../../users/user-tree-item/user-tree-item.component';

@Component({
    selector: 'app-group-tree-item',
    templateUrl: './group-tree-item.component.html',
    styleUrls: ['./group-tree-item.component.scss'],
    imports: [MatIconModule, NgClass, UserTreeItemComponent_1, ContextualMenuComponent_1]
})
export class GroupTreeItemComponent extends DefaultListComponent<Group> implements OnInit {
    /**
     * Indique si les utilisateurs doivent être affichés (true) ou si seuls les groupes d'utilisateurs doivent être affichés (false).
     */
    @Input() isDisplayUser: boolean;

    /**
     * Instance du groupe affiché par le noeud courant.
     */
    @Input() group: Group;

    /**
     * Liste de l'ensemble des rôles.
     * Utilisé pour afficher le libellé du rôle d'un groupe ou d'un utilisateur.
     */
    @Input() roles: Role[];

    /**
     * Liste de l'ensemble des langues.
     * Utilisé pour afficher le libellé de la langue d'un utilisateur.
     */
    @Input() cultures: Culture[];

    /**
     * Indique si le noeud est affiché déplié (true) ou plié (false).
     */
    @Input() expanded: boolean;

    /**
     * Emis lorsque l'utilisateur clique sur le menu contextuel 'Editer' d'un groupe.
     */
    @Output()
    groupEdited: EventEmitter<Group> = new EventEmitter();

    /**
     * Emis lorsque l'utilisateur clique sur le menu contextuel 'Consulter' d'un groupe.
     */
    @Output()
    groupDisplayed: EventEmitter<Group> = new EventEmitter();

    /**
     * Emis lorsque l'utilisateur clique sur le menu contextuel 'Réinitialiser' d'un groupe.
     */
    @Output()
    groupReseted: EventEmitter<Group> = new EventEmitter();

    /**
     * Emis lorsque l'utilisateur clique sur le menu contextuel 'Editer' d'un utilisateur.
     */
    @Output()
    userEdited: EventEmitter<User> = new EventEmitter();

    /**
     * Emis lorsque l'utilisateur clique sur le menu contextuel 'Consulter' d'un utilisateur.
     */
    @Output()
    userDisplayed: EventEmitter<User> = new EventEmitter();

    /**
     * Emis lorsque l'utilisateur clique sur le menu contextuel 'Réinitialiser' d'un utilisateur.
     */
    @Output()
    userReseted: EventEmitter<User> = new EventEmitter();

    /**
     * Liste des noeuds enfants de type groupe.
     */
    public childrenGroups: Group[] = [];

    /**
     * Liste des noeuds enfants de type utilisateurs.
     */
    public childrenUsers: User[] = [];

    /**
     * Libellé du rôle du groupe courant
     */
    public roleName: string;

    /**
     * Valeur indiquant si l'id est affiché.
     */
    public isIdVisible: boolean;

    /**
     * Réference au composant du menu contextuel.
     */
    @ViewChild(ContextualMenuComponent, { static: false })
    contextualMenu: ContextualMenuComponent<Group, MenuITemAction>;

    /**
     * Référence aux composants correspondant aux noeuds enfants de type groupe.
     */
    @ViewChildren(GroupTreeItemComponent)
    groupChildren: QueryList<GroupTreeItemComponent>;

    /**
     * Référence aux composants correspondant aux noeuds enfants de type utilisateurs.
     */
    @ViewChildren(UserTreeItemComponent)
    userChildren: QueryList<UserTreeItemComponent>;

    /**
     * Constructeur.
     * @param dialog Boite de dialogue.
     * @param groupsService Service pour gérer les groupes.
     * @param usersService Service pour gérer les utilisateurs.
     * @param memoryService Service pour acceder aux infos de la session.
     */
    constructor(
        public override dialog: MatDialog,
        public groupsService: GroupsService,
        public usersService: UsersService,
        protected override memoryService: MemoryService
    ) {
        super(dialog, memoryService);

        this.isIdVisible = this.memoryService.getConnectedUser().params.idVisible === true;

        // Spécifie les éléments du menu contextuel en fonction des droits utilisateur
        this.AddMenuItem('Éditer', MenuITemAction.Update, Rights.GroupAndUserEdit);
        this.AddMenuItem('Réinitialiser', MenuITemAction.Delete, Rights.GroupAndUserEdit);
    }

    /**
     * Initialise le composant.
     */
    ngOnInit(): void {
        // Chargement des noeuds enfants.
        this.loadChildren();

        this.determineRoleName();
    }

    /**
     * Détermine le nom du rôle attribué au groupe courant.
     */
    private determineRoleName() {
        if (!!this.roles) {
            var groupRoles = this.roles.filter((x) => this.group.roles.findIndex((r) => r === x.id) != -1);
            this.roleName = groupRoles.map((x) => x.name).join(',');
        }
    }

    /**
     * Charge les noeuds enfants.
     */
    private loadChildren() {
        let context: Context = { property: 'parentGroupId', value: this.group.id };

        this.groupsService.List(context, null, null, null).subscribe((x) => {
            this.childrenGroups = x.value;
        });
        this.groupsService.List(context, null, null, null).subscribe((x) => {
            this.childrenGroups = x.value;
        });

        // Charge les noeuds utilisateurs si c'est spécifié.
        if (this.isDisplayUser) {
            this.usersService.List(context, null, null, null).subscribe((x) => {
                this.childrenUsers = x.value;
            });
        }
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'icône pour plier / déplier le groupe.
     */
    public onExpand(): void {
        if (this.childrenGroups.length !== 0 || this.childrenUsers.length !== 0) {
            this.expanded = !this.expanded;
        }
    }

    /**
     * Affiche le menu contextuel du groupe courant.
     * @param event : Événement souris.
     * @param group : Groupe pour lequel le menu contextuel doit être affiché.
     */
    public displayContextualMenu(event: MouseEvent, group: Group) {
        this.contextualMenu.open(event, group);
    }

    /**
     * Appellé lorsque l'utilisateur clique sur un élément du menu contextuel.
     * @param event : Événement indiquant l'élément du menu contextuel cliqué.
     */
    public onMenuChanged(event: ContextualMenuChange<Group, MenuITemAction>) {
        if (event.action == MenuITemAction.Read) {
            // Cas de la consultation.
            this.groupDisplayed.emit(this.group);
        } else if (event.action == MenuITemAction.Update) {
            // Cas de la mise à jour.
            this.groupEdited.emit(this.group);
        } else if (event.action == MenuITemAction.Delete) {
            // Cas de la réinitialisation --> affichage de la boite de dialogue de confirmation.
            const dialogRef = this.dialog.open(ConfirmDialogComponent, {
                maxWidth: '400px',
                data: {
                    title: 'Etes-vous sûr?',
                    message: `Vous êtes sur le point de réinitialiser le groupe '${this.group.getDisplayName()}'.`,
                },
                panelClass: 'empty-dialog',
            });

            // Attente de la réponse de la boite de dialogue de confirmation
            dialogRef.afterClosed().subscribe((dialogResult) => {
                if (dialogResult) {
                    // Réinitialisation effective du groupe.
                    this.groupReseted.emit(this.group);
                }
            });
        }
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Consulter' dans le menu contextuel d'un noeud enfant de type groupe.
     * @param group Groupe à afficher.
     */
    public onGroupDisplayed(group: Group) {
        this.groupDisplayed.emit(group);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Editer' dans le menu contextuel d'un noeud enfant de type groupe.
     * @param group Groupe à editer.
     */
    public onGroupEdited(group: Group) {
        this.groupEdited.emit(group);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Réinitialiser' dans le menu contextuel d'un noeud enfant de type groupe.
     * @param group Groupe à réinitialiser.
     */
    public onGroupReseted(group: Group) {
        this.groupReseted.emit(group);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Consulter' dans le menu contextuel d'un noeud enfant de type utilisateur.
     * @param user Utilisateur à afficher.
     */
    public onUserDisplayed(user: User) {
        this.userDisplayed.emit(user);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Editer' dans le menu contextuel d'un noeud enfant  de type utilisateur.
     * @param user Utilisateur à editer.
     */
    public onUserEdited(user: User) {
        this.userEdited.emit(user);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Réinitialiser' dans le menu contextuel d'un noeud enfant de type utilisateur.
     * @param user Utilisateur à réinitialiser.
     */
    public onUserReseted(user: User) {
        this.userReseted.emit(user);
    }

    /**
     * Raffraichit l'affichage de ce groupe s'il correspond à l'id spécifié.
     * Fait suivre la demande de raffraichissement aux noeuds enfants.
     * @param groupId : Id du groupe à raffraichir.
     */
    public refreshGroup(groupId: string) {
        if (this.childrenGroups.some((x) => x.id === groupId)) {
            this.loadChildren();
        }

        this.groupChildren.toArray().forEach((item, index) => {
            item.refreshGroup(groupId);
        });
    }

    /**
     * Fait suivre la demande de raffraichissement aux noeuds enfants.
     * @param userId : Id de l'utilisateur à raffraichir.
     */
    public refreshUser(userId: string) {
        if (this.childrenUsers.some((x) => x.id === userId)) {
            this.loadChildren();
        }

        this.groupChildren.toArray().forEach((item, index) => {
            item.refreshUser(userId);
        });

        this.userChildren.toArray().forEach((item, index) => {
            item.refreshUser(userId);
        });
    }
}
